/**
 * Currency Role - Format Pillar
 *
 * Formata valores monetários para apresentação.
 * Usa metadata (código da moeda) para determinar formatação.
 *
 * @example
 * import { formatCurrency } from '@attrx/role-morphic/currency/format';
 *
 * formatCurrency(1500.50, { currency: 'BRL' });  // "R$ 1.500,50"
 * formatCurrency(1000, { currency: 'USD' });     // "$1,000.00"
 * formatCurrency(99.99, { currency: 'EUR' });    // "99,99 €"
 */

import {
  CURRENCY_CONFIGS,
  type CurrencyCode,
  type CurrencyConfig,
} from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface CurrencyFormatOptions {
  /** Código da moeda (obrigatório) */
  currency: CurrencyCode;
  /** Casas decimais (default: decimais padrão da moeda) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Esconde símbolo, retorna só o número formatado */
  hideSymbol?: boolean;
  /** Força símbolo antes do valor */
  symbolFirst?: boolean;
  /** Usa Intl.NumberFormat com locale */
  locale?: string;
  /** Mostra sinal de positivo para valores positivos */
  showPositiveSign?: boolean;
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor monetário para apresentação
 *
 * @param value - Valor numérico
 * @param options - Opções de formatação (currency obrigatório)
 * @returns String formatada
 *
 * @example
 * formatCurrency(1500.50, { currency: 'BRL' });                    // "R$ 1.500,50"
 * formatCurrency(1000, { currency: 'USD' });                       // "$1,000.00"
 * formatCurrency(99.99, { currency: 'EUR' });                      // "99,99 €"
 * formatCurrency(1500, { currency: 'JPY' });                       // "¥1,500"
 * formatCurrency(1500.50, { currency: 'BRL', verbose: true });     // "1.500,50 Brazilian reais"
 * formatCurrency(1500.50, { currency: 'BRL', hideSymbol: true });  // "1.500,50"
 * formatCurrency(1500.50, { currency: 'BRL', locale: 'pt-BR' });   // "R$ 1.500,50" (via Intl)
 */
export function formatCurrency(
  value: number,
  options: CurrencyFormatOptions
): string {
  const config = CURRENCY_CONFIGS[options.currency];

  if (!config) {
    throw new Error(`Unknown currency code: ${options.currency}`);
  }

  // Se locale especificado, usa Intl.NumberFormat
  if (options.locale) {
    return formatWithIntl(value, options, config);
  }

  // Formatação manual
  return formatManual(value, options, config);
}

/**
 * Formata usando Intl.NumberFormat (mais preciso para i18n)
 */
function formatWithIntl(
  value: number,
  options: CurrencyFormatOptions,
  config: CurrencyConfig
): string {
  const decimals = options.decimals ?? config.decimals;

  const intlOptions: Intl.NumberFormatOptions = {
    style: options.hideSymbol ? "decimal" : "currency",
    currency: config.code,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  if (options.verbose) {
    intlOptions.currencyDisplay = "name";
  }

  let formatted = new Intl.NumberFormat(options.locale, intlOptions).format(
    value
  );

  if (options.showPositiveSign && value > 0) {
    formatted = "+" + formatted;
  }

  return formatted;
}

/**
 * Formatação manual (mais controle, sem dependência de locale)
 */
function formatManual(
  value: number,
  options: CurrencyFormatOptions,
  config: CurrencyConfig
): string {
  const decimals = options.decimals ?? config.decimals;
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Formata o número
  const formattedNumber = formatNumber(
    absValue,
    decimals,
    config.thousandsSeparator,
    config.decimalSeparator
  );

  // Monta o resultado
  let result = "";

  // Sinal
  if (isNegative) {
    result += "-";
  } else if (options.showPositiveSign) {
    result += "+";
  }

  // Símbolo e valor
  if (options.hideSymbol) {
    result += formattedNumber;
  } else if (options.verbose) {
    // Modo verbose: "1.500,50 Brazilian reais"
    const name = absValue === 1 ? config.singular : config.plural;
    result += `${formattedNumber} ${name}`;
  } else {
    // Modo normal com símbolo
    const symbolFirst =
      options.symbolFirst !== undefined
        ? options.symbolFirst
        : config.symbolFirst;

    if (symbolFirst) {
      result += `${config.symbol}${formattedNumber}`;
    } else {
      result += `${formattedNumber} ${config.symbol}`;
    }
  }

  return result;
}

/**
 * Formata número com separadores
 */
function formatNumber(
  value: number,
  decimals: number,
  thousandsSeparator: string,
  decimalSeparator: string
): string {
  // Fixa decimais
  const fixed = value.toFixed(decimals);

  // Separa inteiro e decimal
  const [intPart, decPart] = fixed.split(".");

  // Adiciona separador de milhares
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  // Junta com separador decimal
  if (decimals > 0 && decPart) {
    return `${intFormatted}${decimalSeparator}${decPart}`;
  }

  return intFormatted;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna apenas o símbolo da moeda
 *
 * @example
 * getCurrencySymbol('BRL');  // "R$"
 * getCurrencySymbol('USD');  // "$"
 */
export function getCurrencySymbol(code: CurrencyCode): string {
  const config = CURRENCY_CONFIGS[code];
  if (!config) {
    throw new Error(`Unknown currency code: ${code}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome da moeda (singular ou plural)
 *
 * @example
 * getCurrencyName('BRL', 1);   // "Brazilian real"
 * getCurrencyName('BRL', 2);   // "Brazilian reais"
 */
export function getCurrencyName(code: CurrencyCode, count: number = 1): string {
  const config = CURRENCY_CONFIGS[code];
  if (!config) {
    throw new Error(`Unknown currency code: ${code}`);
  }
  return count === 1 ? config.singular : config.plural;
}

/**
 * Retorna casas decimais padrão da moeda
 *
 * @example
 * getCurrencyDecimals('BRL');  // 2
 * getCurrencyDecimals('JPY');  // 0
 */
export function getCurrencyDecimals(code: CurrencyCode): number {
  const config = CURRENCY_CONFIGS[code];
  if (!config) {
    throw new Error(`Unknown currency code: ${code}`);
  }
  return config.decimals;
}
